import React, { useState } from "react"
import { useSelector } from "react-redux"
import Swal from "sweetalert2"
import moment from "moment"
import { FetchNodePostData } from "utils/common-fetchData"
import { checkPermissionsByModule } from "utils/common-helpers"
import StatementEntryModal from "./StatementEntryModal"

// Roles & Responsibilities module ids (tblmodules1) for the two screens —
// created by sql/statement_reconcile_modules.sql. If those ids had to change
// when the SQL was run, change them here only.
export const STATEMENT_MODULES = { bank: 125, card: 126 }

// Permission string index: 2 view, 3 add, 4 edit, 5 delete.
export const useStatementPermissions = moduleId => {
    const roles = useSelector(state => state?.Roles?.apiData)
    return {
        canAdd: checkPermissionsByModule(roles, moduleId, 3),
        canEdit: checkPermissionsByModule(roles, moduleId, 4),
        canDelete: checkPermissionsByModule(roles, moduleId, 5),
    }
}

// Add / Edit / Delete of single statement rows, shared by both screens.
//   apiBase  : "bankStatement" | "creditCardStatement"
//   account  : the selected bank/card option
//   nameKey / idKey : "bank_name"/"bank_id" or "card_name"/"card_id"
//   onChanged: refresh the list + counts after a successful save/delete
export const useStatementEntryActions = ({ apiBase, account, nameKey, idKey, accountWord, token, setLoading, onChanged }) => {
    const [modal, setModal] = useState({ open: false, entry: null })

    const post = async (path, body) => {
        const res = await FetchNodePostData(setLoading, `${apiBase}/entry/${path}`, token, body)
        return res?.data
    }

    const openAdd = () => {
        if (!account) {
            Swal.fire({ icon: "warning", text: `Please select a ${accountWord} first.` })
            return
        }
        setModal({ open: true, entry: null })
    }

    const openEdit = entry => setModal({ open: true, entry })

    const onSave = async values => {
        const data = modal.entry
            ? await post("edit", { entry_id: modal.entry.id, ...values })
            : await post("add", { [nameKey]: account?.[nameKey], [idKey]: account?.value, ...values })
        if (data?.success) {
            Swal.fire({ icon: "success", text: modal.entry ? "Entry updated." : "Entry added.", timer: 1500, showConfirmButton: false })
            onChanged()
            return true
        }
        Swal.fire({ icon: "error", text: data?.message || "Could not save this entry." })
        return false
    }

    const onDelete = async entry => {
        const amount = Number(entry.credit) > 0 ? `credit ₹${Number(entry.credit).toFixed(2)}` : `debit ₹${Number(entry.debit).toFixed(2)}`
        const result = await Swal.fire({
            title: "Delete this entry?",
            text: `${moment(entry.statement_date).format("DD-MM-YYYY")} · ${amount} · ${String(entry.description || "").slice(0, 80)}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        })
        if (!result.isConfirmed) return
        const data = await post("delete", { entry_id: entry.id })
        if (data?.success) {
            Swal.fire({ icon: "success", text: "Entry deleted.", timer: 1500, showConfirmButton: false })
            onChanged()
        } else {
            Swal.fire({ icon: "error", text: data?.message || "Could not delete this entry." })
        }
    }

    const entryModal = (
        <StatementEntryModal
            isOpen={modal.open}
            toggle={() => setModal({ open: false, entry: null })}
            entry={modal.entry}
            accountLabel={account ? `${accountWord === "bank" ? "Bank" : "Card"}: ${account.label}` : ""}
            onSave={onSave}
        />
    )

    return { openAdd, openEdit, onDelete, entryModal }
}
